import React, { useState, useMemo } from 'react'
import { Typography, Popover, Button, Paper } from '@mui/material'
import {
  HoverableLink,
  HighlightedTypography,
} from 'src/components/MUI/StyledComponents'
import CitationViewerModal from 'src/components/CitationViewerModal'
import { MaintenanceItem, Reference } from 'types/graphql'
import { PublicationType } from 'types/graphql'
import { getMaintenanceItemTypeDisplay } from '@wingwork/common/src/maintenanceItem/utils'
import { t } from 'i18next'

interface AtaCodeCellProps {
  row: MaintenanceItem
}

// Separate component for reference content to prevent unnecessary re-renders
const ReferenceContent = React.memo(
  ({
    references,
    onViewCitation,
  }: {
    references: Reference[]
    onViewCitation: (event: React.MouseEvent, reference: Reference) => void
  }) => {
    const categorizedReferences: Record<PublicationType, Reference[]> =
      useMemo(() => {
        return references.reduce((acc, reference) => {
          acc[reference.referenceSource.publicationType] = [
            ...(acc[reference.referenceSource.publicationType] || []),
            reference,
          ]
          return acc
        }, {} as Record<PublicationType, Reference[]>)
      }, [references])

    return (
      <div
        className="max-w-[350px] p-2"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="max-h-[300px] overflow-y-auto">
          {Object.entries(categorizedReferences).map(([type, refs]) => (
            <div key={type} className="mb-4">
              <Typography variant="subtitle1" className="mb-2 font-semibold">
                {t(`referenceSource.publicationType.${type}`)}
              </Typography>
              {refs.map((reference, index) => (
                <div
                  key={reference.id}
                  className={index < refs.length - 1 ? 'mb-4' : ''}
                >
                  <Typography variant="subtitle2" className="font-bold">
                    {reference.name}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    className="line-clamp-3 overflow-ellipsis"
                  >
                    {reference.description}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="mb-1 line-clamp-3 whitespace-pre-line"
                  >
                    {reference.extractedText}
                  </Typography>
                  <Button
                    size="small"
                    onClick={(event) => onViewCitation(event, reference)}
                  >
                    {t(`reference.viewReference.${type}`)}
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    )
  }
)
ReferenceContent.displayName = 'ReferenceContent'

const AtaCodeCell: React.FC<AtaCodeCellProps> = React.memo(({ row }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedReference, setSelectedReference] = useState<Reference | null>(
    null
  )

  const references = row.references
  const hasReference = references && references.length > 0

  // Memoize content string to prevent unnecessary re-renders
  const ataCodeContent = useMemo(
    () => row.formattedAtaManufactureCode,
    [row.formattedAtaManufactureCode]
  )

  const componentDescription = useMemo(
    () => getMaintenanceItemTypeDisplay(row),
    [row.maintenanceType, row.adSbType, row.trackedByComponent?.name]
  )

  const ammReference = useMemo(() => {
    return row?.metadata?.procRef
  }, [row?.metadata])

  const handlePopoverToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const handleViewCitation = (
    event: React.MouseEvent,
    reference: Reference
  ) => {
    event.stopPropagation()
    setSelectedReference(reference)
    handlePopoverClose()
  }

  const handleCloseCitation = (event: React.MouseEvent) => {
    event.stopPropagation()
    setSelectedReference(null)
  }

  // feature check done in the BE
  // If feature is disabled or there's no reference and we're done loading, render minimal content
  if (!hasReference) {
    return (
      <div className="flex flex-col">
        <HighlightedTypography variant="body2">
          {ataCodeContent}
        </HighlightedTypography>
        <Typography variant="body2" className="text-black/60">
          {componentDescription}
        </Typography>
        {ammReference && (
          <Typography variant="body2" className="text-black/60">
            {ammReference}
          </Typography>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <HoverableLink
        variant="body2"
        onClick={handlePopoverToggle}
        className="cursor-pointer"
      >
        {ataCodeContent}
      </HoverableLink>
      <Typography variant="body2" className="text-black/60">
        {componentDescription}
      </Typography>

      <Popover
        id="reference-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        classes={{
          paper: 'rounded-lg',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <ReferenceContent
          references={references}
          onViewCitation={handleViewCitation}
        />
      </Popover>

      <CitationViewerModal
        isOpen={
          !!selectedReference &&
          !!selectedReference.referenceSource?.fileEntry?.url
        }
        reference={selectedReference}
        maintenanceItemId={row.id}
        onClose={handleCloseCitation}
        pageNumbers={selectedReference?.pageNumbers}
      />
    </div>
  )
})

AtaCodeCell.displayName = 'AtaCodeCell'

export default AtaCodeCell
